import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Star, TrendingUp, Plus, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function QualityPicks({ user, onLogout }) {
  const navigate = useNavigate();
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchQualityStocks();
  }, []);

  const fetchQualityStocks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/discover/quality-picks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStocks(response.data.stocks);
    } catch (error) {
      console.error('Error fetching quality stocks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToPortfolio = (stock) => {
    navigate('/investments', { state: { prefillStock: stock } });
  };

  if (loading) {
    return (
      <div className="min-h-screen p-4 lg:p-8">
        <Navigation user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 lg:p-8">
      <Navigation user={user} onLogout={onLogout} />
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center">
              <Star className="w-6 h-6 text-white fill-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-100">Quality Stock Picks</h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400 ml-15">Fundamentally strong stocks auto-screened for quality</p>
        </div>

        {/* Screening Criteria */}
        <Card className="glass p-6 mb-8 dark:bg-gray-800 border-l-4 border-amber-500">
          <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-3">📊 Screening Criteria</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="flex items-center space-x-2">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              <span className="text-gray-700 dark:text-gray-300">ROE &gt; 15%</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              <span className="text-gray-700 dark:text-gray-300">P/E &lt; 60</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              <span className="text-gray-700 dark:text-gray-300">Debt/Equity &lt; 1.0</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
              <span className="text-gray-700 dark:text-gray-300">Quality Score: 0-10</span>
            </div>
          </div>
        </Card>

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-lg font-semibold text-gray-800 dark:text-gray-100">
            {stocks.length} Quality Stocks Found
          </p>
        </div>

        {/* Stock Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {stocks.map((stock, idx) => (
            <Card key={idx} className="glass p-6 dark:bg-gray-800 hover:shadow-lg transition-shadow">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100">{stock.name}</h3>
                    <div className="flex items-center space-x-1">
                      {[...Array(3)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{stock.sector}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-800 dark:text-gray-100">₹{stock.price}</p>
                  <p className={`text-sm font-semibold ${stock.change_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {stock.change_percent >= 0 ? '+' : ''}{stock.change_percent}%
                  </p>
                </div>
              </div>

              {/* Quality Score */}
              <div className="mb-4 p-3 bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Quality Score</span>
                  <span className="text-2xl font-bold text-amber-600 dark:text-amber-400">{stock.quality_score}/10</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-2">
                  <div 
                    className="bg-gradient-to-r from-amber-500 to-yellow-500 h-2 rounded-full transition-all"
                    style={{ width: `${stock.quality_score * 10}%` }}
                  ></div>
                </div>
              </div>

              {/* Metrics Grid */}
              <div className="grid grid-cols-4 gap-3 mb-4 text-sm">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">ROE</p>
                  <p className="font-bold text-green-600 dark:text-green-400">{stock.roe}%</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">P/E</p>
                  <p className="font-bold text-gray-800 dark:text-gray-100">{stock.pe_ratio}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">D/E</p>
                  <p className="font-bold text-blue-600 dark:text-blue-400">{stock.debt_to_equity}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Mkt Cap</p>
                  <p className="font-bold text-gray-800 dark:text-gray-100 text-xs">{stock.market_cap}</p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-2">
                <Button
                  onClick={() => handleAddToPortfolio(stock)}
                  className="flex-1 bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add to Portfolio
                </Button>
                <Button
                  variant="outline"
                  onClick={() => window.open(`https://www.google.com/finance/quote/${stock.symbol}:NSE`, '_blank')}
                  className="dark:bg-gray-700 dark:text-gray-100"
                >
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>

        {stocks.length === 0 && (
          <Card className="glass p-12 text-center dark:bg-gray-800">
            <p className="text-gray-500 dark:text-gray-400">No quality stocks found matching the criteria at this time.</p>
          </Card>
        )}

        {/* Info Box */}
        <Card className="glass p-6 mt-8 dark:bg-gray-800">
          <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4">What Makes These Stocks "Quality"?</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm text-gray-600 dark:text-gray-400">
            <div>
              <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-2">Good ROE (&gt;15%)</h4>
              <p>Company efficiently uses shareholders' money to generate profits. Above 15% ROE indicates strong management and profitability.</p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-2">Reasonable P/E (&lt;60)</h4>
              <p>Stock isn't extremely overvalued. P/E below 60 suggests reasonable pricing, though quality companies can command premium valuations.</p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-2">Manageable Debt (&lt;1.0)</h4>
              <p>Company has manageable debt relative to equity. Debt-to-equity below 1.0 indicates financial stability and lower risk.</p>
            </div>
          </div>
        </Card>

      </div>
      <Footer />
    </div>
  );
}
